/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/PublicationData/PublicationData.tsx.
 */

import React from "react";
import Button from "../../../../../components/common/Button";
import "../../../../../styles/formBase.css";
import "./PublicationData.css";
import { usePublicationData } from "./hooks/usePublicationData";
import PublicationDataFieldRenderer from "./components/PublicationDataFieldRenderer";
import PublicationDataFieldGroups from "./components/PublicationDataFieldGroups";
import { isPublicationFieldDisabled } from "./utils/publicationFieldConditionality";

interface PublicationDataProps {
  submissionId?: string;
  onNextStage: () => void;
}

const PublicationData: React.FC<PublicationDataProps> = ({
  submissionId,
  onNextStage,
}) => {
  const {
    formData,
    values,
    isDirty,
    loading,
    errors,
    handleChange,
    clearFile,
    handleSubmit,
    getAllFields,
  } = usePublicationData(submissionId);

  const handleSave = async () => {
    await handleSubmit();
  };

  const handleNext = async () => {
    const success = await handleSubmit();
    if (success) {
      onNextStage();
    }
  };

  if (loading) return <p className="loading-text">Loading form...</p>;
  if (!formData) return <p>No form data available.</p>;

  const allFields = getAllFields();

  const typeOfPublicationValue = String(values.typeOfPublication ?? "");

  const renderField = (field: any) => {
    const fieldName = String(field.name || "");

    const shouldDisable = isPublicationFieldDisabled({
      fieldName,
      typeOfPublication: typeOfPublicationValue,
      hasDataPublished: String(values.hasDataPublished ?? ""),
    });

    return (
      <PublicationDataFieldRenderer
        key={field.id}
        field={field}
        value={values[fieldName] || ""}
        onChange={handleChange}
        onClearFile={clearFile}
        error={errors[fieldName]}
        disabled={shouldDisable}
      />
    );
  };

  return (
    <div className="form-container publication-form-container contributor-stage-form">
      <h2 className="form-heading">{formData.name}</h2>
      <p className="form-subtext">Please enter/ select data available with you</p>

      <form className="form-content">
        <PublicationDataFieldGroups
          allFields={allFields}
          renderField={renderField}
        />

        <div className="button-row">
          <Button
            label="Save"
            onClick={handleSave}
            type="button"
            disabled={loading || !isDirty}
          />
          <Button
            label="Next"
            onClick={handleNext}
            type="button"
            disabled={loading}
          />
        </div>
      </form>
    </div>
  );
};

/** React component — Publication Data. */
export default PublicationData;
